/**
 * Yazıcı ayarı (sistem rolü): takma ad, varsayılan, ham yazıcı. Takma ad kiracıda tekildir ve
 * SP'nin @EXCPrinterName'i / Worker görev ekinin PrinterName'i bununla eşleşir — cihazı da
 * belirler (DEPO-ETIKET → hangi PC, hangi yazıcı).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Dialog } from '@/shared/ui/Dialog';
import { Input } from '@/shared/ui/Input';
import { Label } from '@/shared/ui/Label';
import { queryKeys } from '@/shared/api/queryKeys';
import { deviceApi, type DevicePrinterDto } from '../api/deviceApi';

export function PrinterEditDialog({ deviceCode, printer, onClose }: { deviceCode: string; printer: DevicePrinterDto; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [alias, setAlias] = useState(printer.printerAlias ?? '');
  const [isDefault, setIsDefault] = useState(printer.isDefault);
  const [isRaw, setIsRaw] = useState(printer.isRawPrinter);
  const save = useMutation({
    mutationFn: () => deviceApi.updatePrinter(printer.devicePrinterId, { printerAlias: alias.trim(), isDefault, isRawPrinter: isRaw }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.device.all });
      onClose();
    },
  });
  const error = save.error ? ((save.error as { message?: string })?.message ?? String(save.error)) : null;

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${deviceCode} / ${printer.printerName}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common:Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            {t('common:Save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="printer-alias">PrinterAlias</Label>
          <Input id="printer-alias" value={alias} onChange={(e) => setAlias(e.target.value.toUpperCase())} placeholder="DEPO-ETIKET" />
          <span className="text-fg-subtle text-xs">{t('shell.user.devicesPrinterAliasHint')}</span>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={isDefault} onCheckedChange={(v) => setIsDefault(v === true)} /> {t('shell.user.devicesPrinterDefault')}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={isRaw} onCheckedChange={(v) => setIsRaw(v === true)} /> {t('shell.user.devicesPrinterRaw')}
        </label>
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}
